import React, { useState, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { ModuleBase, UmbracoForm, Text } from '@/components';
import { isColorDark, fixColor } from '@/utils';
import classNames from 'classnames';

// Bridge: lives inside UmbracoForm's Formik context, pushes the current
// selection into the hidden form fields (arrivalDate/departureDate/tripType).
const BookingFormBridge = ({ selected, tripType }) => {
  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    setFieldValue('arrivalDate', selected?.arrivalDate || '');
    setFieldValue('departureDate', selected?.departureDate || '');
    setFieldValue('tripType', tripType || '');
  }, [selected, tripType, setFieldValue]);

  return null;
};

const statusClass = (status) => {
  const s = status?.toLowerCase();
  if (s === 'limited') return 'bg-bright-orange/[0.125] text-bright-orange';
  if (s === 'sold out') return 'border border-grey3 text-grey3';
  return 'bg-bright-blue/[0.125] text-bright-blue'; // Available (default)
};

const BookingAvailabilityModule = ({ data }) => {
  const { introText, tabs, belowTabsText, formFields } = data || {};
  const isLight = isColorDark(fixColor(data?.backgroundColour));
  const isBlackBorder =
    data?.backgroundColour === '#f7f2e8'
      ? '[&_.input]:border [&_.input]:border-black [&_.input]:focus:border-black [&_.input:focus]:!border-black'
      : '';

  const [activeTab, setActiveTab] = useState(0);
  const [selectedKey, setSelectedKey] = useState(null); // `${tabIndex}-${dateIndex}` — single select across all tabs
  const [selected, setSelected] = useState(null);
  const [selectedTripType, setSelectedTripType] = useState('');

  const onSelectDate = (tabIndex, dateIndex, date) => {
    const key = `${tabIndex}-${dateIndex}`;
    if (selectedKey === key) {
      setSelectedKey(null);
      setSelected(null);
      setSelectedTripType('');
    } else {
      setSelectedKey(key);
      setSelected(date);
      setSelectedTripType(tabs[tabIndex]?.tripType || ''); // lock to the tab of the selected date
    }
  };

  if (!tabs?.length || !formFields) {
    return null;
  }

  return (
    <ModuleBase data={{ ...data }} className="text-black">
      <div className="container px-s sm:max-w-[482px] sm:px-xs lg:max-w-[896px]">
        {introText && (
          <Text
            dangerouslySetInnerHTML={{ __html: introText }}
            className="color-from-bg mb-8 font-larken text-xl font-light leading-7 md:text-desktop-small md:leading-desktop-medium [&_p]:m-0"
          />
        )}

        {/* Tabs — stacked on mobile, horizontal from md */}
        <div className="mb-6 flex flex-col gap-3 md:mb-4 md:flex-row md:flex-wrap md:gap-6">
          {tabs.map((tab, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActiveTab(i)}
              className={classNames(
                'cursor-pointer pb-2 text-left font-gotham text-sm leading-tight transition-colors md:border-b-2 md:text-center md:text-base',
                activeTab === i
                  ? 'text-bright-orange md:border-bright-orange'
                  : 'text-black hover:text-grey3 md:border-transparent',
              )}
              dangerouslySetInnerHTML={{ __html: tab.tabLabel }}
            />
          ))}
        </div>

        {/* Active tab's dates */}
        {tabs.map((tab, tabIndex) => (
          <div key={tabIndex} className={classNames('mb-8', activeTab === tabIndex ? 'block' : 'hidden')}>
            {tab.aboveDatesText && (
              <Text
                dangerouslySetInnerHTML={{ __html: tab.aboveDatesText }}
                className="text-medium mb-4 font-gotham font-s-light leading-6 text-black/75 [&_p]:m-0"
              />
            )}

            {tab.dates?.map((date, dateIndex) => {
              // Heading / divider row — no checkbox, no status
              if (date.sectionHeading) {
                return (
                  <p key={dateIndex} className="text-medium mb-1 mt-4 font-gotham font-n-light leading-6 text-black/75">
                    <strong>{date.sectionHeading}</strong>
                  </p>
                );
              }

              const isSoldOut = date.status?.toLowerCase() === 'sold out';
              const key = `${tabIndex}-${dateIndex}`;
              const isChecked = selectedKey === key;
              const inputId = `date-${tabIndex}-${dateIndex}`;

              return (
                <div
                  key={dateIndex}
                  className={classNames(
                    'relative flex items-center justify-between border-b border-black/10 py-2',
                    isSoldOut && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <input
                    type="checkbox"
                    id={inputId}
                    className="shrink-0 cursor-pointer disabled:cursor-not-allowed"
                    disabled={isSoldOut}
                    checked={isChecked}
                    onChange={() => !isSoldOut && onSelectDate(tabIndex, dateIndex, date)}
                  />
                  <label
                    htmlFor={inputId}
                    className={classNames(
                      'flex-1 pl-3 font-gotham text-sm font-n-light leading-6 text-black/80',
                      isSoldOut ? 'cursor-not-allowed' : 'cursor-pointer',
                    )}
                  >
                    {date.label}
                  </label>
                  <span
                    className={classNames('shrink-0 rounded-sm px-2 py-1 text-xs uppercase', statusClass(date.status))}
                  >
                    {date.status || 'Available'}
                  </span>
                </div>
              );
            })}
          </div>
        ))}

        {/* Below-tabs text (e.g. "Don't see the dates you need? Call us...") */}
        {belowTabsText && (
          <Text
            dangerouslySetInnerHTML={{ __html: belowTabsText }}
            className={classNames(
              'text-medium mb-8 font-gotham font-s-light leading-6 [&_a]:underline [&_p]:mb-2',
              isLight ? 'text-white/75' : 'text-black/75',
            )}
          />
        )}

        {/* Contact form — the bridge is passed as children so it lives inside Formik */}
        <UmbracoForm
          className={classNames(
            '[&_.buttons-container]:mt-m [&_.buttons-container]:sm:justify-center [&_.buttons-container]:lg:justify-start',
            isBlackBorder,
          )}
          successClass="text-center py-10 font-larken text-2xl font-light leading-7 md:text-desktop-medium md:leading-desktop-medium"
          submitBtnClass={classNames('btn secondary w-full sm:w-auto', isLight ? 'light' : 'dark')}
          formDefinition={formFields}
          isLight
        >
          <BookingFormBridge selected={selected} tripType={selectedTripType} />
        </UmbracoForm>
      </div>
    </ModuleBase>
  );
};

export default BookingAvailabilityModule;
